import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance        from './axiosInstance';
import { AuthContext }      from './context/AuthContext';
import NotificationBell     from './components/NotificationBell';
import { launcherGroups }   from './utils/appAccess';
import useHrMe              from './hr/useHrMe';
import './styles/SolumToolSuiteLayout.css';

function SolumToolSuiteLayout({ children, isDropdown = false }) {
  /* ── menu toggle ─────────────────────────────────────────────── */
  const [isOpen, setIsOpen] = useState(false);
  const [toolSuiteOpen, setToolSuiteOpen] = useState(false);
  const [saasSuiteOpen, setSaasSuiteOpen] = useState(false);
  const [unlockMessage, setUnlockMessage] = useState('');
  const dropdownRef         = useRef(null);

  const toggleMenu = () => setIsOpen(prev => !prev);
  const toggleToolSuite = (e) => {
    e.stopPropagation();
    setToolSuiteOpen(prev => !prev);
  };
  const toggleSaasSuite = (e) => {
    e.stopPropagation();
    setSaasSuiteOpen(prev => !prev);
  };

  useEffect(() => {
    /* close if clicked outside */
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ── auth + logout ───────────────────────────────────────────── */
  const { user, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();

  /* ── function definitions from API ────────────────────────────── */
  const [definitions, setDefinitions] = useState([]);

  useEffect(() => {
    axiosInstance.get('/function-definitions')
      .then(({ data }) => setDefinitions(data.functions || []))
      .catch(err => console.error('Failed to load function definitions:', err));
  }, []);

  /* ── accessible menu items, grouped by definition category (not code prefix) ── */
  const groups = launcherGroups(user, definitions);
  const getAccessibleItems = (category) => (category === 'saas' ? groups.saas : groups.tool);

  /* ── Time Off + HR Portal (hardcoded extras, not function codes) ──── */
  /* GET /api/hr/me never blocks the menu; HR Portal appears once it answers isHr. */
  /* Signed-out and pending visitors never call it (the menu also renders for them). */
  const canSeeTimeOff = !!user && user.role !== 'pending';
  const hrMe = useHrMe({ enabled: canSeeTimeOff });
  const canSeeHrPortal = canSeeTimeOff && hrMe.me?.isHr === true;

  async function handleLogout() {
    try {
      await axiosInstance.post('auth/logout');   // clears cookie server-side
      await refreshUser();                       // wipe context client-side
      navigate('/login');
    } catch (err) {
      console.error('Logout failed', err);
    }
  }

  /* ── Easter egg: door unlock ─────────────────────────────────── */
  const handleUnlockDoor = async () => {
    const emailPrefix = user?.email?.toLowerCase().split('@')[0];
    if (emailPrefix !== 'ericolsen' && emailPrefix !== 'mirkocorrales') return;
    setUnlockMessage('');
    try {
      const response = await fetch('/api/unlock-door', {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setUnlockMessage('✓ Door unlocked successfully!');
      } else {
        setUnlockMessage('✗ Failed to unlock door');
      }
    } catch (error) {
      console.error('Error unlocking door:', error);
      setUnlockMessage('✗ Error unlocking door');
    } finally {
      setTimeout(() => setUnlockMessage(''), 3000);
    }
  };

  /* ── get role display text ─────────────────────────────────────── */
  const getRoleDisplay = () => {
    if (user?.isSuperAdmin) return { text: 'Super Admin', className: 'role-super-admin' };
    if (user?.role === 'admin') return { text: 'Admin', className: 'role-admin' };
    if (user?.role === 'user') return { text: '', className: '' }; // Don't show badge for regular users
    if (user?.role === 'pending') return { text: 'Pending', className: 'role-pending' };
    return { text: user?.role || '', className: '' };
  };

  /* ── shared menu markup (ul > li list) ───────────────────────── */
  const MenuList = () => {
    const saasItems = getAccessibleItems('saas');
    const toolItems = getAccessibleItems('tool');
    const roleInfo = getRoleDisplay();

    return (
      <ul>
        <li className="greeting">
          <div className="greeting-line">
            Hello&nbsp;<strong>{user?.displayName || 'friend'}</strong>
            {roleInfo.text && (
              <span className={`role-badge ${roleInfo.className}`}>{roleInfo.text}</span>
            )}
          </div>
          <div className="greeting-actions">
            <Link to="/user-profile" className="profile-link" onClick={() => setIsOpen(false)}>My Profile</Link>
            <span className="greeting-separator">|</span>
            <span onClick={handleLogout} className="logout-link">logout</span>
          </div>
        </li>

        <li onClick={() => setIsOpen(false)}>
          <Link to="/main-homepage">Home</Link>
        </li>

        {canSeeTimeOff && (
          <li onClick={() => setIsOpen(false)}>
            <Link to="/time-off">Time Off</Link>
          </li>
        )}

        {canSeeHrPortal && (
          <li onClick={() => setIsOpen(false)}>
            <Link to="/hr-portal">HR Portal</Link>
          </li>
        )}

        {/* Show suites if user has at least one accessible function */}
        <>
            {/* Solum SaaS Suite - Only show if user has at least one SaaS function */}
            {saasItems.length > 0 && (
              <li className="menu-section">
                <div className="menu-section-header" onClick={toggleSaasSuite}>
                  <span>Solum SaaS Suite</span>
                  <span className="menu-arrow">{saasSuiteOpen ? '▼' : '▶'}</span>
                </div>
                {saasSuiteOpen && (
                  <ul className="submenu">
                    {saasItems.map((item, index) => (
                      <li key={index} onClick={() => setIsOpen(false)}>
                        <Link to={item.path}>{item.label}</Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}

            {/* Solum Tool Suite - Only show if user has at least one On-Prem function */}
            {toolItems.length > 0 && (
              <li className="menu-section">
                <div className="menu-section-header" onClick={toggleToolSuite}>
                  <span>Solum Tool Suite</span>
                  <span className="menu-arrow">{toolSuiteOpen ? '▼' : '▶'}</span>
                </div>
                {toolSuiteOpen && (
                  <ul className="submenu">
                    {toolItems.map((item, index) => (
                      <li key={index} onClick={() => setIsOpen(false)}>
                        <Link to={item.path}>{item.label}</Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
        </>

        {/* ✅ Super Admin-only links */}
        {user?.isSuperAdmin && (
          <li onClick={() => setIsOpen(false)}>
            <Link to="/audit-logs" style={{ color: '#ff6b6b', fontWeight: 'bold' }}>
              Audit & Monitoring
            </Link>
          </li>
        )}

        {/* Eric Olsen private features */}
        {user?.email?.toLowerCase().split('@')[0] === 'ericolsen' && (
          <li onClick={() => setIsOpen(false)}>
            <Link to="/safe-out" style={{ color: '#00c864' }}>
              Safe/Out Toggle
            </Link>
          </li>
        )}
      </ul>
    );
  };

  /* ── DROPDOWN  (mobile / right-hand) ─────────────────────────── */
  if (isDropdown) {
    return (
      <>
        <div className="DropdownMenu DropdownMenu--standalone" ref={dropdownRef}>
          <NotificationBell />
          <button className="DropdownButton" onClick={toggleMenu}>
            ☰ Apps
          </button>

          {isOpen && (
            <div className="DropdownContent">
              <MenuList />
            </div>
          )}
        </div>
        {children}
      </>
    );
  }

  /* ── FULL SIDEBAR  (desktop) ─────────────────────────────────── */
  return (
    <div className="SolumToolSuiteLayout">
      <header className="SolumToolSuiteLayoutHeader">
        <h1
          onClick={handleUnlockDoor}
          style={{ cursor: (['ericolsen', 'mirkocorrales'].includes(user?.email?.toLowerCase().split('@')[0])) ? 'pointer' : 'default' }}
        >
          Solum Tool Suite
        </h1>

        {/* Easter egg unlock message */}
        {['ericolsen', 'mirkocorrales'].includes(user?.email?.toLowerCase().split('@')[0]) && unlockMessage && (
          <div style={{
            position: 'absolute',
            top: '65px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '13px',
            fontWeight: '500',
            color: unlockMessage.startsWith('✓') ? '#16a085' : '#e74c3c',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '8px 16px',
            borderRadius: '8px',
            whiteSpace: 'nowrap',
            zIndex: 1000
          }}>
            {unlockMessage}
          </div>
        )}

        {/* Mobile dropdown menu (hidden on desktop) */}
        <div className="DropdownMenu" ref={dropdownRef}>
          <button className="DropdownButton" onClick={toggleMenu}>
            ☰ Apps
          </button>

          {isOpen && (
            <div className="DropdownContent">
              <MenuList />
            </div>
          )}
        </div>
      </header>

      <div className="SolumToolSuiteLayoutContent">
        <nav className="SolumToolSuiteLayoutSidebar">
          <MenuList />
        </nav>

        <main className="SolumToolSuiteLayoutMain">
          {children}
        </main>
      </div>
    </div>
  );
}

export default SolumToolSuiteLayout;
